// One short haptic tick per route transition, fired by whichever of three occasions notices it first: a TAP
// (tapFeedback), a SWIPE crossing the midpoint (createMidpointHaptic), or a stack TRAVEL (travelFeedback).
// Never on the commit or settle: by then the page has arrived, and a tick after the finger has left reads as a
// delayed rattle rather than as the page changing hands.
// Android uses navigator.vibrate. iOS has no haptics API, so it toggles a real <input type="checkbox" switch>
// (Safari 17.4+): physically, by the user's own tap on an invisible overlay — the one mechanism Apple cannot
// take away, but it needs a tap — or programmatically via label.click(), which iOS 26.5 patched and which a
// swipe must use anyway, having no tap to borrow. None of it is load-bearing.

import type { CSSProperties } from 'react'

/** One tick, 8ms. Short enough to read as a tap; anything longer or repeated reads as an error buzz. */
const TAP_MS = 8

// Only the tick that PLAYS updates it — a suppressed one is not an event. See travelFeedback.
let lastTickAt = -Infinity

// A pile-up lands within a frame or two, so this only has to outlast a couple of frames — and must stay well
// below the cadence of two separate actions: two link taps 160ms apart are two transitions and must be two ticks.
const COINCIDENT_MS = 100

// Both halves matter: without the pointer test a hybrid device could buzz on a mouse click, and without the
// reduced-motion test the tick would fire on a surface the engine has collapsed to a single inert slot.
function hapticsWanted(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(pointer: coarse)').matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

function canVibrate(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function'
}

function vibrateTick(): void {
  try {
    navigator.vibrate(TAP_MS)
  } catch {
    // Chromium throws without user activation, or when the permission is denied.
  }
}

// One per document: off the a11y tree and display: none, so it is not a tab stop and takes no layout.
let hapticSwitch: HTMLLabelElement | null = null

/** Distinctive enough not to collide with app markup — the `for`/`id` pair is load-bearing (see switchTick). */
const SWITCH_ID = 'rt-haptic-switch'

// Ported from web-haptics (lochie/web-haptics, MIT) rather than reinvented, because every detail is load-bearing
// in a way that is invisible off-device: label.click() and not input.click() (the haptic rides the label's
// activation behaviour), `all: initial` + `appearance: auto` to keep the NATIVE appearance that carries the
// system behaviour, and `display: none` after those, since `all: initial` resets display back to inline.
// Works on iOS 17.4-26.4 only; 26.5 patched script-driven activation, and no feature detection can tell — the
// activation succeeds either way and only the haptic is withheld.
// The click deliberately bubbles: stopPropagation() would risk the tick itself, and .click() dispatches no
// pointer events, so outside-press handlers never see it.
function switchTick(): void {
  if (typeof document === 'undefined') return
  if (!hapticSwitch) {
    const label = document.createElement('label')
    label.setAttribute('for', SWITCH_ID)
    label.setAttribute('aria-hidden', 'true')
    label.style.display = 'none'
    const input = document.createElement('input')
    input.type = 'checkbox'
    // The `switch` ATTRIBUTE is the whole feature: a checkbox carrying it behaves as a system switch in Safari.
    input.setAttribute('switch', '')
    input.id = SWITCH_ID
    input.tabIndex = -1
    // On the LABEL and the INPUT both: beyond a11y, it keeps the dispatched click out of the user-action trail,
    // which resolves an aria-hidden target to its parent rather than recording it.
    input.setAttribute('aria-hidden', 'true')
    input.style.all = 'initial'
    input.style.appearance = 'auto'
    input.style.display = 'none'
    label.append(input)
    document.body.append(label)
    hapticSwitch = label
  }
  // Both directions play, and nothing reads the state, so it is free to accumulate.
  hapticSwitch.click()
}

// Android only, on purpose: on iOS the user's finger has already toggled the overlay switch, and a programmatic
// tick here would make every such nav buzz TWICE on the versions where both work.
export function tapFeedback(): void {
  if (!hapticsWanted()) return
  // Recorded even where we cannot vibrate: on iOS this occasion is served by the overlay switch, a tick JS never
  // fires — and leaving lastTickAt unset makes a travelFeedback in the same commit miss the coincidence.
  lastTickAt = performance.now()
  if (canVibrate()) vibrateTick()
}

// For a transition no overlay-carrying control started — the stack's depth changing, and a gesture's landing.
// Takes the PROGRAMMATIC path on iOS, unlike tapFeedback: there is no overlay under the finger here, so
// deferring to one left the hierarchical tick silent on every iOS version.
// Stands down if a tick has just played: a depth change is the one occasion that can be the SAME transition as
// another (a section link tapped while a level is open), and two ticks a frame apart read as a rattle.
export function travelFeedback(): void {
  if (performance.now() - lastTickAt < COINCIDENT_MS) return
  dragTick()
}

// For an occasion with no overlay to borrow: iOS takes the programmatic switch, Android the same vibrate.
function dragTick(): void {
  if (!hapticsWanted()) return
  lastTickAt = performance.now()
  if (canVibrate()) vibrateTick()
  else switchTick()
}

// A finger parked on the seam jitters either side of 0.5 and would rattle the motor once per frame.
// Asymmetric on purpose: the FORWARD edge fires exactly at 0.5, where the chrome flips, and only the return
// edge pays the band.
const MID_BAND = 0.05

// `seam` identifies WHICH pair is being crossed, and a new seam re-arms silently from wherever it starts — so
// paging on through a second boundary ticks on its own midpoint, and a jump to a new pair never ticks for itself.
// One instance per surface: the state is the crossing's, not the app's.
export function createMidpointHaptic(): (seam: string, progress: number) => void {
  let armed: string | null = null
  let past = false
  return (seam, progress) => {
    if (seam !== armed) {
      armed = seam
      past = progress >= 0.5
      return
    }
    if (!past && progress >= 0.5) {
      past = true
      dragTick()
    } else if (past && progress <= 0.5 - MID_BAND) {
      past = false
      dragTick()
    }
  }
}

// Deliberately NOT feature-detected via `'switch' in input`: a detection that silently returned false on the one
// platform this exists for would fail exactly where it must not, and cannot be verified off-device.
// Client-only by nature, so the caller renders the overlay from an effect-set flag, never on the server.
export function switchHapticWanted(): boolean {
  return !canVibrate() && hapticsWanted()
}

// NOT pointer-events: none and NOT inert — it has to actually receive the tap, or there is no haptic. `opacity`
// rather than `appearance: none`, which is liable to take the switch's system behaviour with it.
// KNOWN COSTS on iOS, none verifiable off-device: the long-press link menu no longer opens on these links; a
// native switch tracks a horizontal drag, which is the same gesture as panning a tab strip; and <input> inside
// <a> is invalid per the content model, so a validator will flag it (no browser reparents it).
export const SWITCH_HAPTIC_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  margin: 0,
  opacity: 0,
  cursor: 'pointer',
  touchAction: 'manipulation',
  WebkitTapHighlightColor: 'transparent',
}
